/**
 * Monthly COGS Reconciliation Exception Config
 *
 * Covers: ex-cogs-monthly-incomplete
 *
 * Each row is one outstanding YYYY-MM month from the FP's Monthly COGS
 * Tool. Backend reads from franchise_exception_tags +
 * franchise_cogs_months - no per-row external join. When the FP has no
 * Monthly COGS Tool yet, the backend returns `tool_status: 'missing'`
 * and the empty state below renders the "generate the tool" prompt
 * instead of "no exceptions found". When the FP is on the legacy
 * register, `fp_summary.tool_source === 'legacy_excel'` and a subtle
 * Legacy chip surfaces on the panel metadata strip.
 */
import React from 'react'
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  Link,
  Stack,
  Typography,
} from '@mui/material'
import { OpenInNew as OpenInNewIcon } from '@mui/icons-material'
import { ExcelIcon, GoogleSheetsIcon } from '../../../components/SystemIcons'
import { buildIssuesColumn } from '../shared/issuesColumn'
import { PLATFORM_CONTEXT_COLUMNS } from '../shared/platformColumns'
import { composeRowSubtitle, moneyPart, textPart } from '../shared/rowSubtitle'
import { statusPillChipProps } from '../../../components/shared/AppDrawer/chipPresets'
import { formatBusinessDate } from '../../../utils/inspectorFormatters'
import type {
  ExceptionConfig,
  ExceptionColumnDef,
} from '../types'

const renderMonth = (_value: any, row: Record<string, any>): React.ReactNode => {
  const label = row.month_label || row.month || '-'
  return (
    <Typography component="span" sx={{ fontWeight: 500 }}>
      {label}
    </Typography>
  )
}

const renderStatus = (_value: any, row: Record<string, any>): React.ReactNode => {
  const status = (row.status || '').trim()
  if (!status) return '-'
  const tone =
    status === 'Needs review'
      ? 'error'
      : status === 'Ready to Journal'
        ? 'warning'
        : 'neutral'
  return React.createElement(Chip, statusPillChipProps(status, tone))
}

const renderProductsToReview = (
  _value: any,
  row: Record<string, any>,
): React.ReactNode => {
  const count =
    typeof row.products_to_review === 'number'
      ? row.products_to_review
      : Number(row.products_to_review || 0)
  if (!Number.isFinite(count) || count === 0) return '-'
  return (
    <Typography component="span" sx={{ color: 'error.main', fontWeight: 600 }}>
      {String(count)}
    </Typography>
  )
}

const renderSourceIcon = (source: unknown): React.ReactNode => {
  if (source === 'google') return <GoogleSheetsIcon />
  if (source === 'legacy_excel') return <ExcelIcon />
  return null
}

const renderSheetLink = (
  _value: any,
  row: Record<string, any>,
): React.ReactNode => {
  const url = row.sheet_url
  if (!url) return '-'
  return (
    <Link
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        fontSize: '0.875rem',
      }}
    >
      {renderSourceIcon(row.source)}
      Open sheet
      <OpenInNewIcon fontSize="inherit" />
    </Link>
  )
}

const renderDetectedAt = (
  _value: any,
  row: Record<string, any>,
): React.ReactNode => {
  const detected = row.exception_detected_at
  if (!detected) return '-'
  return formatBusinessDate(detected)
}

const columns: ExceptionColumnDef[] = [
  buildIssuesColumn(),
  ...PLATFORM_CONTEXT_COLUMNS,
  {
    key: 'month',
    label: 'Month',
    align: 'left',
    render: renderMonth,
    getSortValue: (row) => row.month || '',
  },
  {
    key: 'status',
    label: 'Status',
    align: 'center',
    render: renderStatus,
    getSortValue: (row) => row.status || '',
  },
  {
    key: 'products_to_review',
    label: 'Products to Review',
    align: 'center',
    render: renderProductsToReview,
    getSortValue: (row) =>
      typeof row.products_to_review === 'number' ? row.products_to_review : -1,
  },
  {
    key: 'sheet_url',
    label: 'Sheet',
    align: 'center',
    contexts: ['inspector'],
    render: renderSheetLink,
  },
  {
    key: 'exception_detected_at',
    label: 'Detected',
    align: 'center',
    render: renderDetectedAt,
    getSortValue: (row) => row.exception_detected_at || '',
    exportFormat: 'date',
  },
]

const panelMetadata = (response: Record<string, any> | null): React.ReactNode => {
  if (!response) return null
  const summary = (response.fp_summary || {}) as Record<string, any>
  if (response.tool_status === 'missing') return null
  const completion =
    typeof summary.months_loaded === 'number'
      ? `${summary.months_loaded} months loaded`
      : null
  const lastPosted = summary.last_posted_month
    ? `Last posted: ${summary.last_posted_month}`
    : null
  const latestLoaded = summary.latest_month_loaded
    ? `Latest loaded: ${summary.latest_month_loaded}`
    : null
  const inactivity = summary.inactivity_flag
    ? `Activity: ${summary.inactivity_flag}`
    : null
  const evoStatus = summary.evo_status ? `Tool status: ${summary.evo_status}` : null
  const bits = [completion, lastPosted, latestLoaded, inactivity, evoStatus].filter(
    Boolean,
  ) as string[]
  const isLegacy = summary.tool_source === 'legacy_excel'
  if (bits.length === 0 && !isLegacy) return null
  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{ flexWrap: 'wrap', alignItems: 'center', color: 'text.secondary' }}
    >
      {isLegacy ? (
        <Chip
          label="Legacy Excel"
          size="small"
          variant="outlined"
          sx={{ fontSize: '0.7rem', height: 20 }}
        />
      ) : null}
      {bits.map((b, i) => (
        <Typography key={i} component="span" variant="caption">
          {b}
        </Typography>
      ))}
    </Stack>
  )
}

const renderEmptyState = (
  response: Record<string, any> | null,
): React.ReactNode | null => {
  if (!response || response.tool_status !== 'missing') return null
  const summary = (response.fp_summary || {}) as Record<string, any>
  const sheetUrl = summary.sheet_url as string | undefined
  return (
    <Box sx={{ py: 6, px: 3 }}>
      <Alert severity="info" variant="outlined">
        <AlertTitle>Monthly COGS Tool not generated yet</AlertTitle>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {response.message ||
            'No Monthly COGS Tool has been generated for this franchise partner yet. Outstanding months will appear here once the tool is created.'}
        </Typography>
        {sheetUrl ? (
          <Link
            href={sheetUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ fontSize: '0.875rem' }}
          >
            Open linked sheet
          </Link>
        ) : null}
      </Alert>
    </Box>
  )
}

export const monthlyCogsConfig: ExceptionConfig = {
  codes: ['ex-cogs-monthly-incomplete'],
  columns,
  defaultSortColumn: 'month',
  defaultSortDirection: 'desc',
  complexity: 'simple',
  rowKeyField: 'month',
  getRowTitle: (row) =>
    textPart(row.month_label) || textPart(row.month) || 'COGS month',
  getRowSubtitle: (row) =>
    composeRowSubtitle([
      typeof row.fp_count === 'number' && row.fp_count > 0
        ? `${row.fp_count} FPs incomplete`
        : null,
      moneyPart(row.outstanding_amount),
    ]),
  panelMetadata,
  renderEmptyState,
}
